import type { CrowdexCollaboration, CrowdexUIMessage } from '@crowdex/agent-contracts';
import {
  type ConversationPlacement,
  DEFAULT_WORKTREE_SETTINGS,
  type WorktreeSettings,
} from '@crowdex/agent-contracts/api';
import { apiFetch } from '@crowdex/host-kit/renderer/http';
import type { CrowdexSessionRecord } from '@crowdex/session-contracts';
import { isLocalMachine } from '@/lib/local-runtime';
import type {
  ConversationPullRequestBinding,
  ConversationPullRequestClaim,
} from '@/lib/pull-request';

export type LocalConversation = {
  id: string;
  session: CrowdexSessionRecord;
  workspaceId: string;
  workspaceRoot: string;
  workspaceName: string;
  logicalWorkspaceId: string | null;
  /** Its own worktree, where it runs or which it left for the main checkout; null: none. */
  worktree: ConversationWorktree | null;
  agentConfigId: string;
  agent: {
    id: string;
    name: string;
    command: string;
    args: string[];
    env: Record<string, string>;
  };
  title: string;
  runtime: 'local' | 'worktree';
  collaboration: CrowdexCollaboration;
  modelId: string | null;
  status: 'active' | 'archived';
  messages: readonly {
    id: string;
    role: CrowdexUIMessage['role'];
    parts: CrowdexUIMessage['parts'];
    metadata?: CrowdexUIMessage['metadata'] | null;
  }[];
  messageRevision: number;
  createdAt: string;
  updatedAt: string;
  lastMessageAt: string | null;
  /** Free labels the sidebar filters on (the plugin that started it). */
  tags: string[];
  /** False when its folder is not on this machine now (deleted, moved, unmounted). */
  workspaceAvailable?: boolean;
  /** Its workspace, project and parent (absent from servers before workspaces). */
  placement?: ConversationPlacement;
  /** The pull request its work is on in its folder, as the server records it; null: none. */
  pullRequest?: ConversationPullRequestClaim | null;
  /** Its pull requests bound by what it did; `dismissed`: removed by the user. */
  pullRequestBindings?: ConversationPullRequestBinding[];
};

/** A conversation's own managed worktree, as the server records it. */
export type ConversationWorktree = {
  workspaceId: string;
  path: string;
  /** The repository checkout it is a worktree of. */
  sourceRoot: string | null;
  /** False once a cleanup took it off the disk: opening the conversation makes it again. */
  available?: boolean;
};

export type LocalWorkspace = {
  id: string;
  logicalWorkspaceId: string;
  rootPath: string;
  /** The server's name: a worktree's is its repository's. */
  name: string;
  runtime: 'local' | 'worktree';
  /** For a worktree, the repository checkout it belongs to. */
  sourceWorkspaceId: string | null;
  sourceRoot: string | null;
  updatedAt: string;
  /** False when the folder is not on this machine now (deleted, moved, unmounted). */
  availableOnDevice?: boolean;
};

const JSON_HEADERS = { 'content-type': 'application/json' } as const;

function conversationPath(id: string): string {
  return `/api/local/conversations/${encodeURIComponent(id)}`;
}

/*
 * This machine's conversations and workspaces, stored by the local Crowdex
 * server (`/api/local/*`). Outside the local profile there are none.
 */

export function listLocalWorkspaces(): Promise<LocalWorkspace[]> {
  if (!isLocalMachine()) return Promise.resolve([]);
  return apiFetch<LocalWorkspace[]>('/api/local/workspaces');
}

/**
 * Records a folder of this machine. The server names it (a worktree after its repository) and
 * keeps its name after; `name` only names a new one.
 */
export function saveLocalWorkspace(input: {
  id?: string;
  logicalWorkspaceId?: string;
  rootPath: string;
  name?: string;
  runtime?: 'local' | 'worktree';
}): Promise<LocalWorkspace> {
  return apiFetch<LocalWorkspace>('/api/local/workspaces', {
    method: 'PUT',
    headers: JSON_HEADERS,
    body: JSON.stringify(input),
  });
}

export function listLocalConversations(): Promise<LocalConversation[]> {
  if (!isLocalMachine()) return Promise.resolve([]);
  return apiFetch<LocalConversation[]>('/api/local/conversations');
}

export function getLocalConversation(id: string): Promise<LocalConversation | null> {
  if (!isLocalMachine()) return Promise.resolve(null);
  return apiFetch<LocalConversation | null>(conversationPath(id));
}

/** Records the pull request opened from a conversation (its Create PR dialog) as its own. */
export function claimLocalConversationPullRequest(
  id: string,
  claim: ConversationPullRequestClaim,
): Promise<ConversationPullRequestClaim> {
  return apiFetch<ConversationPullRequestClaim>(`${conversationPath(id)}/pull-request`, {
    method: 'PUT',
    headers: JSON_HEADERS,
    body: JSON.stringify(claim),
  });
}

/** Where this machine keeps its managed worktrees, the same for every client of its server. */
export function readWorktreeSettings(): Promise<WorktreeSettings> {
  if (!isLocalMachine()) return Promise.resolve(DEFAULT_WORKTREE_SETTINGS);
  return apiFetch<WorktreeSettings>('/api/local/worktree-settings');
}

export function saveWorktreeSettings(settings: WorktreeSettings): Promise<WorktreeSettings> {
  return apiFetch<WorktreeSettings>('/api/local/worktree-settings', {
    method: 'PUT',
    headers: JSON_HEADERS,
    body: JSON.stringify(settings),
  });
}
